import { PollStatusType } from "@/types/graphql/site.generated";
import type { PollHostType, SitePoll } from "../model/types";

/** Закрытый опрос эфира с результатами висит 15 секунд (спека). */
export const CLOSED_POLL_VISIBLE_MS = 15000;

export interface SelectPollArgs {
  polls: SitePoll[];
  hostType: PollHostType;
  currentTime: number;
  nowMs: number;
  dismissedIds: ReadonlySet<string>;
  /** Когда витрина узнала о закрытии (время клиента). */
  closedAtById: ReadonlyMap<string, number>;
  /** Опросы, на которые зритель ответил в этой сессии. */
  votedInSessionIds: ReadonlySet<string>;
}

/**
 * Какой опрос показать. Видео: последний, чья минута наступила; уже
 * отвеченный раньше на повторном просмотре не всплывает. Эфир: открытый,
 * иначе недавно закрытый.
 */
export function selectPollToShow(args: SelectPollArgs): SitePoll | null {
  const visible = args.polls.filter((poll) => !args.dismissedIds.has(poll.id));

  if (args.hostType === "VIDEO") {
    const due = visible.filter(
      (poll) =>
        poll.offsetSeconds !== null &&
        poll.offsetSeconds !== undefined &&
        poll.offsetSeconds <= args.currentTime &&
        (!poll.viewerVote?.length || args.votedInSessionIds.has(poll.id)),
    );
    return due.reduce<SitePoll | null>(
      (best, poll) =>
        !best || (poll.offsetSeconds ?? 0) >= (best.offsetSeconds ?? 0)
          ? poll
          : best,
      null,
    );
  }

  const active = visible.filter(
    (poll) => poll.status === PollStatusType.ActiveType,
  );
  if (active.length > 0) return active[active.length - 1] ?? null;

  const recentlyClosed = visible.filter((poll) => {
    const closedAt = args.closedAtById.get(poll.id);
    return (
      poll.status === PollStatusType.ClosedType &&
      closedAt !== undefined &&
      closedAt + CLOSED_POLL_VISIBLE_MS > args.nowMs
    );
  });
  return recentlyClosed[recentlyClosed.length - 1] ?? null;
}

/** Статус опроса на сервере только растёт: черновик, открыт, закрыт. */
function statusRank(status: SitePoll["status"]): number {
  if (status === PollStatusType.ClosedType) return 2;
  if (status === PollStatusType.ActiveType) return 1;
  return 0;
}

/**
 * Событие со статусом ниже текущего запоздало (флаш и закрытие публикуют
 * по разным соединениям Redis) и отбрасывается целиком, иначе закрытый
 * опрос снова открылся бы и спрятал результаты.
 */
export function mergePoll(polls: SitePoll[], poll: SitePoll): SitePoll[] {
  const current = polls.find((item) => item.id === poll.id);
  if (!current) return [...polls, poll];
  if (statusRank(poll.status) < statusRank(current.status)) return polls;
  return polls.map((item) => (item.id === poll.id ? poll : item));
}
